import Skeleton from '@mui/material/Skeleton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import { useCallback, useEffect, useMemo, useState } from 'react';
import BackendApiService from "../services/BackendApiService";

import TableHead from '@mui/material/TableHead';
import { ApiResource } from '@shared/interfaces/ApiInterfaces';
import { MappedModuleWithChildren } from 'interfaces/Interfaces';
import { getAccessToken } from 'utils/accessTokenStore';
import ExpandableUserResourceRow from './ExpandableUserResourceRow';
import './UserResources.css';

const PAGE_SIZE = 20;

type UserResourcesProps = {
  status?: string;
  sinceDays?: number;
  platformFilters?: string[];
  resourceTypeFilters?: string[];
  statusFilters?: string[];
  resourceFilter?: string | null;
  showAllResourcesFilter?: boolean;
  showSucceededResourcesFilter?: boolean;
  showDeletedResourcesFilter?: boolean;
  loadAll?: boolean;
  expandAll: boolean;
  onLoadingChange?: (loading: boolean) => void;
  onAvailableResourceTypesChange?: (resourceTypes: string[]) => void;
  onScopeTotalCountChange?: (count: number) => void;
  onSummaryCountsChange?: (counts: Record<string, number>) => void;
}

const countRequestNodes = (requests: MappedModuleWithChildren[]): number => {
  const countNode = (node: MappedModuleWithChildren): number => 1 + (node.children?.reduce((total, child) => total + countNode(child), 0) || 0);
  return requests.reduce((total, request) => total + countNode(request), 0);
};

const collectResourceTypes = (requests: MappedModuleWithChildren[]): string[] => {
  const resourceTypes = new Set<string>();

  const visit = (request: MappedModuleWithChildren) => {
    const resourceType = request.name?.toLowerCase().trim();
    if (resourceType) {
      resourceTypes.add(resourceType);
    }

    request.children?.forEach(child => visit(child));
  };

  requests.forEach(request => visit(request));

  return Array.from(resourceTypes).sort((left, right) => left.localeCompare(right));
};

const classifyRequestStatus = (request: MappedModuleWithChildren): string => {
  const description = (request.statusDescription || '').toLowerCase();
  const statusType = (request.statusType || '').toLowerCase();

  if (description.includes('not tracked')) {
    return 'not-tracked';
  }

  if (description.includes('imported')) {
    return 'imported';
  }

  if (description.includes('created')) {
    return 'created';
  }

  if (description.includes('modified')) {
    return 'modified';
  }

  if (statusType === 'import') {
    return 'imported';
  }

  if (statusType === 'create') {
    return 'created';
  }

  if (statusType === 'modify') {
    return 'modified';
  }

  if (statusType === 'delete' && request.prStatus === 'completed') {
    return 'deleted';
  }

  return 'other';
};

const buildSummaryCounts = (requests: MappedModuleWithChildren[]): Record<string, number> => {
  const summary: Record<string, number> = {
    total: 0,
    modified: 0,
    created: 0,
    imported: 0,
    'not-tracked': 0
  };

  const visit = (request: MappedModuleWithChildren) => {
    summary.total += 1;
    const statusKey = classifyRequestStatus(request);
    if (summary[statusKey] !== undefined) {
      summary[statusKey] += 1;
    }
    request.children?.forEach(child => visit(child));
  };

  requests.forEach(request => visit(request));
  return summary;
};

const stripHtml = (value: string): string => value.replace(/<[^>]+>/g, ' ');

const normalizeSearchText = (value: unknown): string => {
  if (value == null) {
    return '';
  }

  return stripHtml(String(value)).toLowerCase().trim();
};

const compactSearchText = (value: string): string => value.replace(/[^a-z0-9]+/g, '');

const requestMatchesAnyFilter = (
  request: MappedModuleWithChildren,
  resourceFilter: string,
  platformFilters: string[],
  resourceTypeFilters: string[],
  statusFilters: string[]
): boolean => {
  const requestPlatform = normalizeSearchText(request.platform);
  const requestType = normalizeSearchText(request.name);
  const prefixedResourceName = `${request.parentName?.prefix || ''}${request.resourceName || ''}${request.parentName?.suffix || ''}`;
  const requestText = normalizeSearchText([
    request.platform || '',
    request.title || '',
    request.displayResourceName || '',
    (request as any).displayName || '',
    request.resourceName || '',
    request.resolvedResourceName || '',
    prefixedResourceName,
    request.parent?.resource || ''
  ].join(' '));
  const requestTextCompact = compactSearchText(requestText);
  const normalizedFilter = normalizeSearchText(resourceFilter);
  const compactFilter = compactSearchText(normalizedFilter);
  const requestStatus = classifyRequestStatus(request);

  const matchesText =
    !normalizedFilter ||
    requestText.includes(normalizedFilter) ||
    (compactFilter.length > 0 && requestTextCompact.includes(compactFilter));
  const matchesPlatform = platformFilters.length === 0 || platformFilters.includes(requestPlatform);
  const matchesType = resourceTypeFilters.length === 0 || resourceTypeFilters.includes(requestType);
  const matchesStatus = statusFilters.length === 0 || statusFilters.includes(requestStatus);

  return matchesText && matchesPlatform && matchesType && matchesStatus;
};

const pruneTreeByFilters = (
  request: MappedModuleWithChildren,
  resourceFilter: string,
  platformFilters: string[],
  resourceTypeFilters: string[],
  statusFilters: string[]
): MappedModuleWithChildren | null => {
  const prunedChildren = (request.children || [])
    .map(child => pruneTreeByFilters(child, resourceFilter, platformFilters, resourceTypeFilters, statusFilters))
    .filter((child): child is MappedModuleWithChildren => Boolean(child));

  const selfMatches = requestMatchesAnyFilter(request, resourceFilter, platformFilters, resourceTypeFilters, statusFilters);

  if (!selfMatches && prunedChildren.length === 0) {
    return null;
  }

  return {
    ...request,
    children: prunedChildren
  };
};

const createTree = (entries: ApiResource[]) => {
  const modulesById: { [key: string]: MappedModuleWithChildren } = {};
  const rootModules: MappedModuleWithChildren[] = [];

  const toTimestamp = (value: Date | string | undefined): number => {
    if (!value) {
      return 0;
    }

    const ts = new Date(value).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };

  const shouldReplaceExisting = (existing: MappedModuleWithChildren | undefined, incoming: ApiResource): boolean => {
    if (!existing) {
      return true;
    }

    return toTimestamp(incoming.updatedDate) > toTimestamp(existing.updatedDate);
  };

  const normalize = (value?: unknown) => {
    if (typeof value === 'string') {
      return value.toLowerCase().trim();
    }

    if (value == null) {
      return '';
    }

    return String(value).toLowerCase().trim();
  };
  const buildParentLookupKey = (name?: string, platform?: string, resource?: string) => (
    `${normalize(name)}|${normalize(platform)}|${normalize(resource)}`
  );

  const getParentResourceCandidates = (resource: MappedModuleWithChildren) => {
    const candidates = new Set<string>();
    const rawResourceName = normalize(resource.resourceName);
    if (rawResourceName) {
      candidates.add(rawResourceName);
    }

    const prefixedName = normalize(`${resource.parentName?.prefix || ''}${resource.resourceName || ''}${resource.parentName?.suffix || ''}`);
    if (prefixedName) {
      candidates.add(prefixedName);
    }

    const resolvedResourceName = normalize(resource.resolvedResourceName);
    if (resolvedResourceName) {
      candidates.add(resolvedResourceName);
    }

    const displayResourceName = normalize(resource.displayResourceName);
    if (displayResourceName) {
      candidates.add(displayResourceName);
    }

    return candidates;
  };

  // First, map all modules by their ID for easy access
  entries.forEach(entry => {
    entry.modules.forEach(module => {
      if (shouldReplaceExisting(modulesById[module.id], entry)) {
        modulesById[module.id] = { ...entry, ...module, children: [] };
      }
    });
  });

  const modules = Object.values(modulesById);
  const parentLookupByKey = new Map<string, string>();

  modules.forEach(module => {
    const parentName = normalize(module.name);
    const parentPlatform = normalize(module.platform);
    if (!parentName || !parentPlatform) {
      return;
    }

    getParentResourceCandidates(module).forEach(candidate => {
      if (candidate) {
        parentLookupByKey.set(buildParentLookupKey(parentName, parentPlatform, candidate), module.id);
      }
    });
  });

  // Then, build the tree structure
  modules.forEach(module => {
    if (module.parent) {
      const parentId = parentLookupByKey.get(buildParentLookupKey(module.parent?.name, module.parent?.platform, module.parent?.resource));
      if (parentId && modulesById[parentId]) {
        modulesById[parentId].children?.push(module);
      } else {
        // parent not found - create "unknown" dummy parent for UI visualization
        const dummyId = `${module.parent.platform}-${module.parent.name}-${module.parent.resource}`
        let parentDummyModule = rootModules.find(m => m.id === dummyId);
        if (!parentDummyModule) {
          const createdParentDummyModule: MappedModuleWithChildren = {
            ...module.parent,
            id: dummyId,
            resourceName: module.parent.resource || dummyId,
            title: `<span style="text-transform: capitalize">${module.parent.name}</span> (${module.parent.resource})`,
            status: undefined,
            statusType: undefined,
            prStatus: undefined,
            statusDescription: "Parent resource is not tracked",
            isEditAllowed: false,
            requester: "",
            children: [],
            // Additional properties
            modules: [],
            checks: {
              review: {
                status: "unknown",
                reviewers: [],
              }
            },
            pullRequestId: 0,
            latestPullRequestId: 0,
            creationDate: new Date(0),
            updatedDate: new Date(0),
            closedDate: new Date(0)
          };
          rootModules.push(createdParentDummyModule);
          parentDummyModule = createdParentDummyModule;
        }

        if (parentDummyModule) {
          parentDummyModule.children.push(module);
        }
      }
    } else {
      rootModules.push(module);
    }
  });

  const getNewestUpdatedDateInTree = (node: MappedModuleWithChildren): number => {
    const ownUpdatedDate = node.updatedDate ? new Date(node.updatedDate).getTime() : 0;
    if (!node.children?.length) {
      return ownUpdatedDate;
    }

    const newestChildDate = Math.max(...node.children.map(child => getNewestUpdatedDateInTree(child)));
    return Math.max(ownUpdatedDate, newestChildDate);
  };

  rootModules.sort((a, b) => getNewestUpdatedDateInTree(b) - getNewestUpdatedDateInTree(a));

  return rootModules;
};

function UserResources(props: UserResourcesProps) {
  const [accessToken] = useState(() => {
    return getAccessToken();
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [userRequests, setUserRequests] = useState<MappedModuleWithChildren[]>([]);
  const [rawEntries, setRawEntries] = useState<ApiResource[]>([]);
  const [loadingDetailsForId, setLoadingDetailsForId] = useState<string | null>(null);
  const [failedDetailsIds, setFailedDetailsIds] = useState<Set<string>>(new Set());
  const [expandAll, setExpandAll] = useState<boolean>(props.expandAll);

  const status = props.status || "active";

  const isRequestVisibleByStatus = useCallback((request: MappedModuleWithChildren): boolean => {
    const includeCancelled = props.showSucceededResourcesFilter ? request.status !== "cancelled" : true;
    const isCompletedDelete = request.statusType === "delete" && request.prStatus === "completed";
    const includeDeleted = props.showDeletedResourcesFilter ? !isCompletedDelete : true;
    return includeCancelled && includeDeleted;
  }, [props.showSucceededResourcesFilter, props.showDeletedResourcesFilter]);

  const getUserRequests = async (accessToken: string | null, status: string, sinceDays: number | undefined, showAllResourcesFilter: boolean | undefined) => {
    setLoading(true);
    props.onLoadingChange?.(true);
    try {
      const backendApiService = new BackendApiService(accessToken ?? '');
      const userRequests = await backendApiService.getUserRequests(
        status,
        sinceDays,
        showAllResourcesFilter,
        !props.showSucceededResourcesFilter,
        !props.showDeletedResourcesFilter,
        undefined,
        !props.loadAll
      );

      const userRequestsTree = createTree(userRequests);

      setRawEntries(userRequests);
      setUserRequests(userRequestsTree);
      props.onScopeTotalCountChange?.(countRequestNodes(userRequestsTree));
      setLoading(false);
      props.onLoadingChange?.(false);
    } catch (err) {
      console.log(err);
      props.onScopeTotalCountChange?.(0);
      setLoading(false);
      props.onLoadingChange?.(false);
    }
  };

  // Only used in windowed mode: resolves the full details for a parent row that was rendered as
  // "Not tracked" because its Create PR fell outside the default 30-day window.
  const loadParentDetails = async (request: MappedModuleWithChildren) => {
    setLoadingDetailsForId(request.id);
    try {
      const backendApiService = new BackendApiService(accessToken ?? '');
      const resolved = await backendApiService.getUserRequestParentDetails(request.platform, request.name, request.resourceName);
      if (resolved) {
        const updatedEntries = [...rawEntries, resolved];
        setRawEntries(updatedEntries);
        setUserRequests(createTree(updatedEntries));
      } else {
        setFailedDetailsIds(current => new Set(current).add(request.id));
      }
    } catch (err) {
      console.log(err);
      setFailedDetailsIds(current => new Set(current).add(request.id));
    } finally {
      setLoadingDetailsForId(null);
    }
  };

  useEffect(() => {
    getUserRequests(accessToken, status, props.sinceDays, props.showAllResourcesFilter);

  }, [
    accessToken,
    status,
    props.sinceDays,
    props.showAllResourcesFilter,
    props.showSucceededResourcesFilter,
    props.showDeletedResourcesFilter,
    props.loadAll
  ]);


  useEffect(() => {
    setExpandAll(props.expandAll);
  }, [props.expandAll]);

  useEffect(() => {
    props.onAvailableResourceTypesChange?.(collectResourceTypes(userRequests));
  }, [userRequests, props.onAvailableResourceTypesChange]);

  useEffect(() => {
    props.onSummaryCountsChange?.(buildSummaryCounts(userRequests));
  }, [userRequests, props.onSummaryCountsChange]);

  const filteredRequests = useMemo(() => {
    const resourceFilter = props?.resourceFilter?.toLowerCase().trim() || "";
    const platformFilters = (props.platformFilters || []).map(value => value.toLowerCase().trim()).filter(Boolean);
    const resourceTypeFilters = (props.resourceTypeFilters || []).map(value => value.toLowerCase().trim()).filter(Boolean);
    const statusFilters = (props.statusFilters || []).map(value => value.toLowerCase().trim()).filter(Boolean);

    const visibleRequests = userRequests.filter(request => isRequestVisibleByStatus(request));

    const hasFacetFilters = resourceFilter || platformFilters.length > 0 || resourceTypeFilters.length > 0 || statusFilters.length > 0;
    if (!hasFacetFilters) {
      return visibleRequests;
    }

    return visibleRequests
      .map(request => pruneTreeByFilters(request, resourceFilter, platformFilters, resourceTypeFilters, statusFilters))
      .filter((request): request is MappedModuleWithChildren => Boolean(request));
  }, [
    userRequests,
    props.resourceFilter,
    props.platformFilters,
    props.resourceTypeFilters,
    props.statusFilters,
    isRequestVisibleByStatus
  ]);

  // Only the full (loadAll) dataset is large enough to need lazy loading; the default 30-day batch always renders in full.
  const [page, setPage] = useState<number>(1);

  useEffect(() => {
    setPage(1);
  }, [filteredRequests, props.loadAll]);

  useEffect(() => {
    if (!props.loadAll) {
      return;
    }

    const handleScroll = () => {
      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 300;
      if (nearBottom) {
        setPage(current => (current * PAGE_SIZE < filteredRequests.length ? current + 1 : current));
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [props.loadAll, filteredRequests.length]);

  const visibleRequests = props.loadAll ? filteredRequests.slice(0, page * PAGE_SIZE) : filteredRequests;

  const activeFilterParts = [
    props.resourceFilter?.trim(),
    ...(props.platformFilters || []),
    ...(props.resourceTypeFilters || []),
    ...(props.statusFilters || [])
  ].filter((value): value is string => Boolean(value));

  const noResultsMessage = activeFilterParts.length > 0
    ? `No resources found for filter: ${activeFilterParts.join(' | ')}`
    : 'No open requests found!';

  return (
    <div>
      <TableContainer>
        <Table sx={{ width: '100%', minWidth: 650, maxWidth: 1600, margin: 'auto', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell width={10}></TableCell>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell width={190}><strong>Reviewer(s)</strong></TableCell>
              <TableCell width={230}><strong>Requester</strong></TableCell>
              <TableCell width={100}><strong>Requested At</strong></TableCell>
              <TableCell width={150}><strong>Status</strong></TableCell>
              <TableCell width={40}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              (!loading && filteredRequests.length === 0) &&
              <TableRow>
                <TableCell colSpan={7}>
                  {noResultsMessage}
                </TableCell>
              </TableRow>
            }
            {visibleRequests.map(request => (
              <ExpandableUserResourceRow
                key={request.id}
                request={request}
                isDefaultExpanded={expandAll}
                resourceFilter={props.resourceFilter}
                platformFilter={''}
                showSucceededResourcesFilter={props.showSucceededResourcesFilter}
                showDeletedResourcesFilter={props.showDeletedResourcesFilter}
                onDeleteSuccess={() => getUserRequests(accessToken, status, props.sinceDays, props.showAllResourcesFilter)}
                onLoadParentDetails={loadParentDetails}
                loadingDetailsForId={loadingDetailsForId}
                failedDetailsIds={failedDetailsIds}
              />)
            )}

            {loading && [0, 1, 2].map(i => (
              <TableRow
                key={i}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell><Skeleton height={30} /></TableCell>
                <TableCell><Skeleton height={30} /></TableCell>
                <TableCell><Skeleton width={30} height={30} variant="circular" /></TableCell>
                <TableCell><Skeleton width={30} height={30} variant="circular" /></TableCell>
                <TableCell><Skeleton height={30} /></TableCell>
                <TableCell><Skeleton width={30} height={30} variant="circular" /></TableCell>
                <TableCell><Skeleton width={30} height={30} /></TableCell>
              </TableRow>
            ))}

          </TableBody>
        </Table>
      </TableContainer>
    </div >
  );
}

export default UserResources;
